import { TFunction } from "i18next";
import qs from "qs";

import { VehicleAgeTimeline } from "shared/api/api";
import { getChartAxisLabel } from "shared/schemas/utils/schema-utils";
import { EventTypeEnum } from "shared/types";
import { randomID } from "shared/utils";

import {
  INVESTIGATOR_EVENT_FILTER_KEY,
  INVESTIGATOR_EVENT_TYPE_KEY,
  INVESTIGATOR_NAVIGATED_FROM_KEY,
  INVESTIGATOR_VEHICLE_FILTER_KEY,
  INVESTIGATOR_VIEW_TITLE_KEY,
} from "pages/Investigator/constants";
import {
  AnyDateDropdownOptions,
  LandingPageChartConfig,
  LandingPageViewConfig,
} from "pages/shared/Dashboard/types";

import { DataElement } from "features/ui/charts/types";
import {
  FilterGroupState,
  FilterRowState,
} from "features/ui/Filters/FilterBuilder/types";
import { getFiltersQuery } from "features/ui/Filters/FilterBuilder/utils";
import { AdvancedFilterConfig } from "features/ui/Filters/StaticFilters/constants";
import { FilterOperator } from "features/ui/Filters/types";
import { SchemaEntry } from "features/ui/MaterialTable";

import {
  DASHBOARD_EVENT_FILTERS_LOCALSTORAGE_KEY,
  DASHBOARD_EVENT_TYPE_LOCALSTORAGE_KEY,
  DASHBOARD_VEHICLE_FILTERS_LOCALSTORAGE_KEY,
} from "./constants";

type DateLabelVariant = "plain" | "last" | "in_last";

const DATE_PERIOD_UNIT: Record<string, string> = {
  d: "day",
  m: "month",
  w: "week",
  y: "year",
};

export const getLastDateFilterLabelString = (
  inputValue: string,
  t: TFunction,
  variant: DateLabelVariant = "plain"
) => {
  const value = inputValue.slice(0, -1);
  const unit = DATE_PERIOD_UNIT[inputValue.slice(-1)];

  if (!unit) return t("common:not_available");

  return t(`common:date_period.${variant}.${unit}`, {
    count: parseInt(value),
  });
};

export const getInitialVehicleDateFieldFilter = (
  config: LandingPageViewConfig
): FilterRowState => {
  const values = [
    config.defaultVehicleDateValue ?? config.vehicleDateFieldOptions[0] ?? "1d",
  ];
  const attribute = config.vehicleDateField ?? "createdAt";

  return {
    id: randomID(),
    type: "row",
    attribute,
    operator: FilterOperator.IN_LAST,
    values,
  };
};

interface ByVehicleAgeMetrics {
  dateIndex: number;
  lastValues: Record<string, number>;
}

// Gets the last non-null value for each exposure bucket in the config
// and the date when the main exposure bucket had the last non-null value.
export const getByVehicleAgeMetrics = (
  config: LandingPageChartConfig,
  data: VehicleAgeTimeline[]
): ByVehicleAgeMetrics | undefined => {
  const yAxisOption = config.selectedChartOptions?.find(
    (option) => option.id === "y"
  )?.optionId;

  if (!yAxisOption) return;

  const output: ByVehicleAgeMetrics = {
    dateIndex: 0,
    lastValues: {},
  };

  const buckets = config.exposureBuckets.map((bucket) =>
    !isNaN(parseFloat(bucket)) ? parseFloat(bucket) : bucket
  );

  const mainBucket = !isNaN(parseFloat(config.mainExposureBucket))
    ? parseFloat(config.mainExposureBucket)
    : config.mainExposureBucket;

  for (let i = 0; i < data.length; i++) {
    const item = data[i];

    if (item.exposureBucket === mainBucket) {
      output.dateIndex = i;
      if (item[yAxisOption as keyof VehicleAgeTimeline] != null) {
        output.lastValues[mainBucket] = item[
          yAxisOption as keyof VehicleAgeTimeline
        ] as number;
      }

      continue;
    }

    if (buckets.includes(item.exposureBucket)) {
      if (item[yAxisOption as keyof VehicleAgeTimeline] != null) {
        output.lastValues[item.exposureBucket] = item[
          yAxisOption as keyof VehicleAgeTimeline
        ] as number;
      }
    }
  }

  return output;
};

export const getDashboardChartTicks = (data: DataElement[]) => {
  if (!data || data.length === 0) return [];

  const timestamps = data.map((item) => item.ts);

  // if 5 or fewer elements, display all
  if (timestamps.length <= 5) {
    return timestamps;
  }

  const ticks = new Set<number>();

  // always add first and last
  ticks.add(timestamps[0]);
  ticks.add(timestamps[timestamps.length - 1]);

  const addMiddleTicks = (startIdx: number, endIdx: number, depth: number) => {
    const length = endIdx - startIdx + 1;

    if (length < 3) return;

    // limit to 3 middle ticks total (depth 0 and 1 only)
    if (depth > 1) return;

    if (length % 2 === 1) {
      // odd number of elements: add the middle one
      const middleIdx = startIdx + Math.floor(length / 2);
      ticks.add(timestamps[middleIdx]);

      // check if we should add ticks between start-middle and middle-end
      const leftLength = middleIdx - startIdx + 1;
      const rightLength = endIdx - middleIdx + 1;

      // if odd number of elements between start and middle, add the middle one
      if (leftLength % 2 === 1 && leftLength >= 3) {
        addMiddleTicks(startIdx, middleIdx, depth + 1);
      }

      // if odd number of elements between middle and end, add the middle one
      if (rightLength % 2 === 1 && rightLength >= 3) {
        addMiddleTicks(middleIdx, endIdx, depth + 1);
      }
    } else {
      // for even length, we want to split into 3 roughly equal parts (2 middle ticks)
      const third = Math.floor(length / 3);
      const firstMiddleIdx = startIdx + third;
      const secondMiddleIdx = endIdx - third;

      ticks.add(timestamps[firstMiddleIdx]);
      ticks.add(timestamps[secondMiddleIdx]);
    }
  };

  addMiddleTicks(0, timestamps.length - 1, 0);

  // convert to a sorted array
  return Array.from(ticks).sort((a, b) => a - b);
};

// Builds the query string for navigating to the Investigator from a dashboard
// view. Callers pass the already-merged event filters (e.g. with a smart-group
// clause for the ungrouped view).
export const getDashboardInvestigatorSearch = ({
  eventFilters,
  vehiclesFilters,
  eventType,
  viewTitle,
}: {
  eventFilters: FilterGroupState;
  vehiclesFilters: FilterGroupState;
  eventType: EventTypeEnum;
  viewTitle: string;
}) =>
  qs.stringify({
    [INVESTIGATOR_EVENT_FILTER_KEY]: getFiltersQuery(eventFilters),
    [INVESTIGATOR_VEHICLE_FILTER_KEY]: getFiltersQuery(vehiclesFilters),
    [INVESTIGATOR_NAVIGATED_FROM_KEY]: "dashboard",
    [INVESTIGATOR_EVENT_TYPE_KEY]: eventType,
    [INVESTIGATOR_VIEW_TITLE_KEY]: viewTitle,
  });

export const saveDashboardStateToLocalStorage = (
  eventFilters: string,
  vehicleFilters: string,
  eventType: EventTypeEnum
) => {
  localStorage.setItem(DASHBOARD_EVENT_FILTERS_LOCALSTORAGE_KEY, eventFilters);
  localStorage.setItem(
    DASHBOARD_VEHICLE_FILTERS_LOCALSTORAGE_KEY,
    vehicleFilters
  );

  localStorage.setItem(DASHBOARD_EVENT_TYPE_LOCALSTORAGE_KEY, eventType);
};

export const getAdvancedFiltersDatePeriod = (
  advancedFilters: AdvancedFilterConfig[] | undefined,
  parentAttribute: string = "lineExits",
  childAttribute: string = "exitTime"
): AnyDateDropdownOptions | undefined => {
  if (!advancedFilters || advancedFilters.length === 0) {
    return undefined;
  }

  const matchingFilter = advancedFilters.find(
    (filter) => filter.parentAttribute === parentAttribute
  );

  if (!matchingFilter) {
    return undefined;
  }

  const defaultOption =
    matchingFilter.childDateDefaultOptions?.[childAttribute];
  const selectedOption =
    matchingFilter.childDateSelectedOptions?.[childAttribute];

  return selectedOption ?? defaultOption;
};

interface ByVehicleAgeXAxisArgs {
  config: LandingPageViewConfig;
  // The relation-path birthday extracted from the live vehicle filter, empty
  // when that relation isn't currently filtered.
  byVehicleAgeBirthday?: string;
  configuredBirthdayField: string;
  vehiclesSchema?: SchemaEntry[];
}

// Names the vehicle date the by-vehicle-age x-axis buckets on, mirroring how
// useCharts picks it: the advanced-filter relation date when one is active,
// otherwise the chart option's own birthday field.
export const getByVehicleAgeXAxisLabel = ({
  config,
  byVehicleAgeBirthday,
  configuredBirthdayField,
  vehiclesSchema,
}: ByVehicleAgeXAxisArgs): string | undefined =>
  getChartAxisLabel(
    byVehicleAgeBirthday
      ? {
          schema: vehiclesSchema,
          relationParentAttribute: config.advancedFilterDateParentAttribute,
          relationChildAttribute: config.advancedFilterDateChildAttribute,
        }
      : { schema: vehiclesSchema, field: configuredBirthdayField }
  );
